import { ApolloClient, Context, ObservableQuery } from '@apollo/client'
import type { DispatchWithoutAction } from 'react'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  from,
  ignoreElements,
  interval,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs'
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators'

import {
  Exact,
  GetCustomerCartDocument,
  GetCustomerCartQuery,
  GetGuestCartDocument,
  GetGuestCartQuery,
  SetCartAttributesDocument,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { fromObservableQuery } from '../../../utils/rx/observable-query'
import { latestAccessToken$ } from '../../auth/state'
import { latestDispensary$ } from '../../dispensaries/state'
import type { Dispensary } from '../../dispensaries/types'
import { latestApolloClient$ } from '../../graphql/apollo'
import type { CartGetState } from '../reducers/cart-get-state'
import { cartQueueActive$, enqueueCartWrite } from './cart-write-queue'

/**
 * The whole reducer state, not just the `UseCartGet` projection the cart context exposes.
 *
 * `mergeStatus` is the field that lets a mutation tell a settled cart from one the guest-to-customer
 * merge is still rewriting. Without it the only available gate was `loading`, which narrows the
 * window rather than closing it (sc-142551, sc-142675).
 */
export type CartMirrorState = CartGetState & { refetch: DispatchWithoutAction }

/**
 * Holds the cart state mirrored from `useCartGet`.
 *
 * A `BehaviorSubject` rather than a `bind`/`state`, whose refCount discards the held value when
 * subscribers reach zero. Consumers here are mutations that must read the current cart at the
 * moment they run, so the value has to outlive any gap in subscribers.
 */
const cartStateSubject$ = new BehaviorSubject<CartMirrorState | null>(null)

export const setCartState = (state: CartMirrorState): void => cartStateSubject$.next(state)

/** useCartGet state */
export const cartState$: Observable<CartMirrorState> = cartStateSubject$.pipe(
  filter(isNotNull),
  // compare only the fields consumers read: adding the rest would emit on every reducer transition
  distinctUntilChanged(
    (a, b) =>
      a.cart === b.cart &&
      a.loading === b.loading &&
      a.error === b.error &&
      a.refetch === b.refetch &&
      a.type === b.type &&
      a.mergeStatus === b.mergeStatus &&
      a.guestCartId === b.guestCartId,
  ),
)

/**
 * Get observable query from Apollo client for customer's cart
 */
const getCustomerCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  token: string,
): ObservableQuery<GetCustomerCartQuery, Exact<{ [key: string]: never }>> =>
  client.watchQuery({
    query: GetCustomerCartDocument,
    context: { token },
    // override global error policy
    errorPolicy: 'none',
  })

const watchGetCustomerCartFactory = (client: ApolloClient<unknown>, token: string) =>
  fromObservableQuery(getCustomerCartObservableQueryFactory(client, token)).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

/**
 * Combine latest Apollo client with access token and return the customer's cart, watching for updates.
 *
 * NOTE: this does not support guest carts.
 */
export const latestCustomerCart$: Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }
> = combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
  switchMap(([client, token]) => watchGetCustomerCartFactory(client, token)),
)

/**
 * Combine latest Apollo client with access token and refetch the customer's cart once.
 */
export const refetchCustomerCart$ = ({ context }: { context?: Context } = {}): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }
> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) =>
      from(
        client.query({
          query: GetCustomerCartDocument,
          context: { token, ...context },
          errorPolicy: 'none',
          fetchPolicy: 'network-only',
        }),
      ).pipe(
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      ),
    ),
  )

export interface CartRefetchOptions {
  /**
   * Drop a tick while a cart write is queued or running, so a poll never reads, or reconciles, a
   * cart mid-write. Off by default: an `interval(0)` that skipped ticks would spin until the
   * queue drained.
   */
  pauseWhileCartQueueActive?: boolean
}

const cartRefetchTicks$ = (
  milliseconds: number,
  { pauseWhileCartQueueActive = false }: CartRefetchOptions,
): Observable<unknown> =>
  pauseWhileCartQueueActive
    ? interval(milliseconds).pipe(
        withLatestFrom(cartQueueActive$),
        filter(([, queueActive]) => !queueActive),
      )
    : interval(milliseconds)

/**
 * Combine latest Apollo client with access token and refetch the customer's cart with the given interval.
 */
export const refetchCustomerCartWithInterval$ = (
  milliseconds: number,
  options: CartRefetchOptions = {},
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) => {
      const obsQuery = getCustomerCartObservableQueryFactory(client, token)
      return cartRefetchTicks$(milliseconds, options).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

/**
 * BehaviorSubject to track the current guest cart ID
 */
export const latestGuestCartId$ = new BehaviorSubject<string | null>(null)

/**
 * Update the guest cart ID observable
 */
export const updateGuestCartId = (cartId: string | null): void => {
  latestGuestCartId$.next(cartId)
}

/**
 * Get observable query from Apollo client for guest cart
 */
const getGuestCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  cartId: string,
): ObservableQuery<GetGuestCartQuery, Exact<{ cartId: string }>> =>
  client.watchQuery({
    query: GetGuestCartDocument,
    variables: { cartId },
    // override global error policy
    errorPolicy: 'none',
  })

/**
 * Combine latest Apollo client with guest cart ID and refetch the guest cart with the given interval.
 */
export const refetchGuestCartWithInterval$ = (
  milliseconds: number,
  options: CartRefetchOptions = {},
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetGuestCartQuery }> =>
  combineLatest([latestApolloClient$, latestGuestCartId$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, cartId]) => {
      const obsQuery = getGuestCartObservableQueryFactory(client, cartId)
      return cartRefetchTicks$(milliseconds, options).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

type SuccessfulCartFetch = {
  _tag: 'Right'
  data: GetCustomerCartQuery | GetGuestCartQuery
}

/**
 * Correct a cart associated with the *wrong* practitioner: when the user is in a dispensary context,
 * align the cart's practitioner code with that dispensary's code (e.g. after shopping across
 * multiple dispensaries). Decides from the just-fetched `result`, so nothing is queued when the code
 * already matches; a due write goes through the cart write queue against the settled cart. Emits
 * `result` without waiting for the write, so a `take(1)` caller is never held behind other queued
 * cart writes and a later poll never waits on an earlier write. A failed write is logged and
 * swallowed so the caller's refetch stream is never torn down.
 *
 * We deliberately do NOT clear the practitioner code in global context. Per sc-132622
 * (https://app.shortcut.com/designsforhealth/story/132622#activity-141484): without guest redirects
 * the lingering-code case isn't a concern right now, so a cart that leaves a dispensary keeps its
 * code. This function only ever sets a real dispensary code, never blanks one.
 */
export const reconcileCartPractitionerCode = (
  result: SuccessfulCartFetch,
  dispensary: Dispensary | null | undefined,
): Observable<SuccessfulCartFetch> => {
  // customer query -> `customerCart`, guest query -> `cart`
  const cart = 'customerCart' in result.data ? result.data.customerCart : result.data.cart

  if (!cart) {
    return of(result)
  }

  // Only reconcile toward a dispensary's code. In global context (no dispensary) — or a dispensary
  // without a code — leave the cart untouched; we never clear the practitioner code (sc-132622).
  const targetPractitionerCode = dispensary?.practitionerCode
  if (!targetPractitionerCode) {
    return of(result)
  }

  // Already correct — nothing to do (and avoids re-firing the mutation on every poll tick).
  if ((cart.practitioner_code ?? '') === targetPractitionerCode) {
    return of(result)
  }

  const write$ = enqueueCartWrite(
    'practitioner-code-reconcile',
    ({ client, cart: target, token }) =>
      // the target may be a merged cart that `result` predates
      (target.practitioner_code ?? '') === targetPractitionerCode
        ? EMPTY
        : defer(() =>
            client.mutate({
              mutation: SetCartAttributesDocument,
              variables: { cartId: target.id, practitionerCode: targetPractitionerCode },
              ...(token && { context: { token } }),
            }),
          ).pipe(
            // the mutation returns the full `...cart` fragment, which Apollo normalizes into the cache
            ignoreElements(),
            // caught inside the job so it is logged even after a `take(1)` caller has unsubscribed
            catchError((error: unknown) => {
              logAndCaptureException(error)
              return EMPTY
            }),
          ),
  ).pipe(
    // the target could not be resolved (seen only while the caller is still subscribed)
    catchError((error: unknown) => {
      logAndCaptureException(error)
      return EMPTY
    }),
  )

  // queued before `result` is emitted, so a `take(1)` caller that unsubscribes on it keeps the write
  return merge(write$, of(result))
}

/**
 * Universal cart refetch with interval that works for both customer and guest carts.
 * Uses customer cart refetch if access token is available, otherwise uses guest cart refetch.
 */
export const refetchCartWithInterval$ = (
  milliseconds: number,
  options: CartRefetchOptions = {},
): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery | GetGuestCartQuery }
> =>
  combineLatest([latestAccessToken$, latestGuestCartId$]).pipe(
    switchMap(([accessToken, guestCartId]) => {
      // If we have an access token, use customer cart refetch
      if (accessToken) {
        return refetchCustomerCartWithInterval$(milliseconds, options)
      }
      // If we have a guest cart ID, use guest cart refetch
      if (guestCartId) {
        return refetchGuestCartWithInterval$(milliseconds, options)
      }
      // If neither, return empty observable (no cart to refetch)
      return EMPTY
    }),
    withLatestFrom(latestDispensary$),
    // Reconcile only successful fetches; a `Left` (failed refetch) must pass through untouched so
    // downstream `take(1)` consumers (e.g. the checkout machine's item-edit refetch) still get an emission
    // and don't hang. This stream never errors — `coerceCaughtToLeft` already turned failures into
    // `Left` next-notifications upstream.
    // `mergeMap`: a later refetch must not wait on an earlier reconcile's queued write
    mergeMap(([result, dispensary]) =>
      result._tag === 'Right' ? reconcileCartPractitionerCode(result, dispensary) : of(result),
    ),
  )
